import React, { useCallback, useMemo, useState } from "react";

import { faArrowDownShortWide } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import { defineMessages, useIntl } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import { Button, SelectField } from "@mapmycustomers/ui";

import useAnalytics from "@app/util/contexts/useAnalytics";
import resourceFieldModel from "@app/util/fieldModel/ResourceFieldModel";

import getSortDirectionNameForField from "./getSortDirectionNameForField";
import getSortFieldDisplayName, { SortField } from "./getSortFieldDisplayName";
import styles from "./ListOrderSelect.module.scss";

const messages = defineMessages({
  apply: {
    id: "component.resources.orderSelect.sort.apply",
    defaultMessage: "Apply",
    description: "Apply sort button",
  },
  cancel: {
    id: "component.resources.orderSelect.sort.cancel",
    defaultMessage: "Cancel",
    description: "Cancel sort button",
  },
  sortCriteria: {
    id: "component.resources.orderSelect.sort.criteria",
    defaultMessage: "Sort Criteria",
    description: "Sort Criteria",
  },
  sortDirection: {
    id: "component.resources.orderSelect.sort.direction",
    defaultMessage: "Sort Direction",
    description: "Sort Direction",
  },
});

interface Props {
  onChange: (field: IField, order: SortOrder) => void;
  sort: SortModel<IField>;
}

const ListOrderSelect = ({ onChange, sort }: Props) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState<SortOrder>(sort[0]?.order ?? SortOrder.ASC);
  const [field, setField] = useState<SortField>(
    (sort[0]?.field.name as SortField) ?? SortField.CREATED_AT
  );

  const sortOptions = useMemo(
    () =>
      Object.values(SortField).map((field) => ({
        label: getSortFieldDisplayName(intl, field),
        value: field,
      })),
    [intl]
  );

  const sortOrderOptions = useMemo(
    () =>
      [SortOrder.ASC, SortOrder.DESC].map((order) => ({
        label: getSortDirectionNameForField(intl, field, order),
        value: order,
      })),
    [field, intl]
  );

  const handleCancel = useCallback(() => {
    setOpen(false);
    setOrder(sort[0]?.order);
    setField(sort[0]?.field.name as SortField);
  }, [sort]);

  const handleApply = useCallback(() => {
    setOpen(false);
    const fieldDef = resourceFieldModel.getByName(field);
    if (fieldDef) {
      onChange(fieldDef, order);
    }
  }, [field, onChange, order]);

  const content = (
    <div className={styles.container}>
      <SelectField<SortField>
        label={intl.formatMessage(messages.sortCriteria)}
        onChange={setField}
        options={sortOptions}
        value={field}
      />
      <SelectField<SortOrder>
        label={intl.formatMessage(messages.sortDirection)}
        onChange={setOrder}
        options={sortOrderOptions}
        value={order}
      />
      <div className={styles.buttons}>
        <Button onClick={handleCancel}>{intl.formatMessage(messages.cancel)}</Button>
        <Button onClick={handleApply} type="primary">
          {intl.formatMessage(messages.apply)}
        </Button>
      </div>
    </div>
  );

  const handleClick = useCallback(() => {
    analytics.clicked(["Sort"]);
  }, [analytics]);

  return (
    <Popover content={content} onOpenChange={setOpen} open={open} trigger={["click"]}>
      <Button
        className={styles.sort}
        leftIcon={<FontAwesomeIcon icon={faArrowDownShortWide} />}
        onClick={handleClick}
      >
        {getSortFieldDisplayName(intl, field)}
      </Button>
    </Popover>
  );
};

export default ListOrderSelect;
